<template>
    <v-card class="rounded_card cf__card">
        <v-card-text>
            <div class="cf__head">
                <h3 class="mb-0">Focus topics</h3>
                <span class="cf__pill">{{ rows.length }} {{ rows.length === 1 ? "topic" : "topics" }}</span>
            </div>
            <p class="cf__muted mt-1 mb-0">{{ intro }}</p>

            <p v-if="showMeasured && trackingLoading" class="cf__status" role="status">Measuring visibility in the tracked answers…</p>
            <p v-else-if="showMeasured && trackingError" class="cf__status cf__status--error" role="alert">{{ trackingError }}</p>
            <ul v-if="rows.length" class="cf__list">
                <li v-for="row in rows" :key="row.key" class="cf__row">
                    <span class="cf__verdict" :class="'cf__verdict--' + row.verdict" :title="verdictTitle(row.verdict)">{{ verdictLabel(row.verdict) }}</span>
                    <div class="cf__body">
                        <span class="cf__topic">{{ row.text }}</span>
                        <span v-if="row.passage" class="cf__passage"><span class="cf__passagelabel">Would be cited:</span> “{{ row.passage }}”</span>
                        <span v-if="row.gap" class="cf__gap">{{ row.gap }}</span>
                    </div>
                    <div v-if="showMeasured" class="cf__measured">
                        <coverage-measured :row="measuredRow(row)" :loading="trackingLoading" :days="trackingDays" :project-id="projectId" :unavailable-text="trackingError || 'Measured visibility unavailable'" />
                    </div>
                </li>
            </ul>
            <div v-else class="cf__empty">No focus topics were set for this audit.</div>

            <div v-if="footnote" class="cf__note">{{ footnote }}</div>
        </v-card-text>
    </v-card>
</template>

<script>
    // Every focus topic of the run, covered ones included: the content-fit verdict of the
    // opened page with the passage that would be cited, and next to it what the AI answers
    // of the linked project actually show (CoverageMeasured). Without an opened page
    // (multi-URL run) the merged verdicts across all pages are listed instead.
    import CoverageMeasured from "./CoverageMeasured.vue"
    import { coverageKey } from "./coverageText"

    export default {
        name: "CoverageFocus",
        components: { CoverageMeasured },
        props: {
            run: { type: Object, default: null },
            // the opened result (null on a multi-URL run with no page opened)
            result: { type: Object, default: null },
            // fallback rows merged across all pages: [{ text, verdict, gap }]
            mergedFocus: { type: Array, default: () => [] },
            projectId: { type: String, default: "" },
            // measured rows by coverageKey(text), from POST /coverage/tracking
            tracking: { type: Object, default: () => ({}) },
            trackingLoading: { type: Boolean, default: false },
            trackingDays: { type: Number, default: 90 },
            trackingError: { type: String, default: "" },
            isMultiUrl: { type: Boolean, default: false }
        },
        computed: {
            focusEntries() {
                const list = this.run && Array.isArray(this.run.focus) ? this.run.focus : []
                return list.map((entry) => (typeof entry === "string" ? entry.trim() : "")).filter(Boolean)
            },
            resultRowsByKey() {
                const map = {}
                const coverage = this.result && this.result.coverage && typeof this.result.coverage === "object" ? this.result.coverage : null
                const list = coverage && Array.isArray(coverage.focus) ? coverage.focus : []
                list.forEach((entry) => {
                    if (!entry || typeof entry !== "object") return
                    const key = coverageKey(entry.query || entry.prompt)
                    if (key && !map[key]) map[key] = entry
                })
                return map
            },
            mergedRowsByKey() {
                const map = {}
                ;(Array.isArray(this.mergedFocus) ? this.mergedFocus : []).forEach((entry) => {
                    const key = coverageKey(entry && entry.text)
                    if (key && !map[key]) map[key] = entry
                })
                return map
            },
            // run order, one row per focus entry; a topic the model returned no verdict for is "unjudged"
            rows() {
                const seen = {}
                const rows = []
                const verdicts = ["covered", "partial", "missing"]
                this.focusEntries.forEach((text) => {
                    const key = coverageKey(text)
                    if (!key || seen[key]) return
                    seen[key] = true
                    const source = this.result ? this.resultRowsByKey[key] : this.mergedRowsByKey[key]
                    const verdict = source && verdicts.indexOf(source.verdict) > -1 ? source.verdict : "unjudged"
                    rows.push({
                        key,
                        text,
                        verdict,
                        passage: this.result && source && typeof source.passage === "string" ? source.passage.trim() : "",
                        gap: source && typeof source.gap === "string" ? source.gap.trim() : ""
                    })
                })
                return rows
            },
            showMeasured() {
                return !!(this.projectId && this.result)
            },
            intro() {
                if (!this.projectId) {
                    return "Content fit per topic: whether this page answers it, judged by AI from the page content. Link the audit to an AI project to also see whether AI assistants cite this page or mention your brand for these topics."
                }
                if (!this.result) {
                    return "Content fit per topic, merged across all audited pages (best verdict wins). Open a page in the URLs tab to see the passage that would be cited and the measured visibility of that page."
                }
                return "Content fit per topic is the AI judgement of this page. Next to it: what the AI answers tracked in your project actually show for the same prompt — answers, citations of this URL and brand mentions."
            },
            footnote() {
                if (!this.showMeasured) return ""
                if (this.trackingError) return ""
                return "Measured over the last " + this.trackingDays + " days of tracked answers. Only prompts tracked in the AI project carry answers; free-text focus topics show as not tracked."
            }
        },
        methods: {
            verdictLabel(verdict) {
                if (verdict === "covered") return "Covered"
                if (verdict === "partial") return "Partial"
                if (verdict === "missing") return "Missing"
                return "Not judged"
            },
            verdictTitle(verdict) {
                if (verdict === "covered") return "Content fit: an AI assistant could answer this citing the page."
                if (verdict === "partial") return "Content fit: the page answers only part of it."
                if (verdict === "missing") return "Content fit: the page holds no usable answer."
                return "The model returned no verdict for this topic."
            },
            measuredRow(row) {
                const match = this.tracking && this.tracking[row.key]
                return match && typeof match === "object" ? match : null
            }
        }
    }
</script>

<style lang="scss" scoped>
    .cf__card {
        box-shadow: 0 2px 18px rgba(0, 0, 0, 0.06) !important;
        margin-top: 20px;
    }
    .cf__head {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
    }
    .cf__muted {
        font-size: 13px;
        color: #3d6d66;
    }
    .cf__pill {
        display: inline-flex;
        align-items: center;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        padding: 3px 10px;
        border-radius: 999px;
        border: 1px solid #d4ede9;
        color: #3d6d66;
    }
    .cf__list {
        margin: 14px 0 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
    }
    .cf__status {
        margin: 10px 0 0;
        font-size: 12px;
        color: #3d6d66;
    }
    .cf__status--error {
        color: #b32d2d;
    }
    .cf__row {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 10px 0;
        border-top: 1px solid #eaf5f3;
        font-size: 13px;
        color: #0d2622;
    }
    .cf__row:first-child {
        border-top: 0;
    }
    .cf__verdict {
        flex: 0 0 76px;
        text-align: center;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        padding: 3px 0;
        border-radius: 999px;
        background: #eef3f2;
        color: #3d6d66;
        margin-top: 2px;
    }
    .cf__verdict--covered {
        background: #e0f5f2;
        color: #007a68;
    }
    .cf__verdict--partial {
        background: #fff3df;
        color: #8a5200;
    }
    .cf__verdict--missing {
        background: #fde4e4;
        color: #b32d2d;
    }
    .cf__body {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 3px;
        line-height: 1.45;
    }
    .cf__topic {
        font-weight: 600;
    }
    .cf__passage {
        font-size: 12px;
        color: #2d5a54;
        font-style: italic;
    }
    .cf__passagelabel {
        font-style: normal;
        font-weight: 600;
        color: #3d6d66;
    }
    .cf__gap {
        font-size: 12px;
        color: #8a5200;
    }
    .cf__measured {
        flex: 0 0 260px;
        max-width: 260px;
        min-width: 0;
    }
    @media (max-width: 760px) {
        .cf__row {
            flex-wrap: wrap;
        }
        .cf__measured {
            flex: 1 1 100%;
            max-width: none;
            padding-left: 88px;
        }
    }
    .cf__empty,
    .cf__note {
        margin-top: 12px;
        font-size: 12px;
        color: #3d6d66;
    }
</style>
